/**
 * Blocos politicos: Mandates (trajetoria), ApprovedProjects (leis), Gallery,
 * Social. Reaproveitam componentes existentes em ../templates/.
 */

/* eslint-disable react-refresh/only-export-components */

import { Landmark, Image as ImageIcon, Globe, FileText, MapPin } from 'lucide-react';
import type { BlockDefinition, BlockRenderProps } from './types';
import { BlockShell } from './BlockShell';
import { GallerySection } from '../templates/GallerySection';
import { SocialLinks } from '../templates/SocialLinks';
import { ApprovedProjectsSection } from '../templates/ApprovedProjectsSection';
import { formatMandate } from '../templates/types';
import type {
  GalleryImage,
  PoliticalMandate,
  ApprovedProject,
  SocialLinks as SocialLinksType,
} from '@/types/tenant.types';

// =============================================================================
// MandatesBlock
// =============================================================================

interface MandatesProps {
  title?: string;
  items?: PoliticalMandate[];
}

function MandatesRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as MandatesProps;
  const items = props.items || ctx.tenant.publicPage.content.mandates || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-8"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Trajetória política'}
        </h2>
        <ul className="space-y-3">
          {items.map((mandate, i) => (
            <li
              key={i}
              className="flex items-center gap-4 p-4 rounded-2xl bg-white border-l-4 shadow-sm"
              style={{ borderColor: theme.primaryColor }}
            >
              <MapPin
                className="h-5 w-5 flex-shrink-0"
                style={{ color: theme.primaryColor }}
              />
              <span className="text-ink-700 font-medium">{formatMandate(mandate)}</span>
            </li>
          ))}
        </ul>
      </div>
    </BlockShell>
  );
}

export const mandatesBlockDefinition: BlockDefinition<MandatesProps> = {
  type: 'mandates',
  label: 'Trajetória política',
  description: 'Mandatos anteriores',
  category: 'content',
  multiple: false,
  icon: Landmark,
  defaultProps: { title: 'Trajetória política' },
  Render: MandatesRender,
};

// =============================================================================
// ApprovedProjectsBlock
// =============================================================================

interface ApprovedProjectsBlockProps {
  title?: string;
  items?: ApprovedProject[];
  variant?: 'default' | 'cards' | 'timeline';
}

function ApprovedProjectsRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as ApprovedProjectsBlockProps;
  const items = props.items || ctx.tenant.publicPage.content.approvedProjects || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-8"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Projetos aprovados'}
        </h2>
        <ApprovedProjectsSection
          projects={items}
          primaryColor={theme.primaryColor}
          variant={props.variant || 'cards'}
        />
      </div>
    </BlockShell>
  );
}

export const approvedProjectsBlockDefinition: BlockDefinition<ApprovedProjectsBlockProps> = {
  type: 'approved-projects',
  label: 'Projetos aprovados',
  description: 'Leis e projetos aprovados',
  category: 'content',
  multiple: false,
  icon: FileText,
  defaultProps: { title: 'Projetos aprovados', variant: 'cards' },
  Render: ApprovedProjectsRender,
};

// =============================================================================
// GalleryBlock
// =============================================================================

interface GalleryBlockProps {
  title?: string;
  items?: GalleryImage[];
}

function GalleryRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as GalleryBlockProps;
  const items = props.items || ctx.tenant.publicPage.content.gallery || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        <h2
          className="font-display text-3xl md:text-4xl font-extrabold mb-8"
          style={{ color: theme.primaryColor }}
        >
          {props.title || 'Galeria'}
        </h2>
        <GallerySection images={items} />
      </div>
    </BlockShell>
  );
}

export const galleryBlockDefinition: BlockDefinition<GalleryBlockProps> = {
  type: 'gallery',
  label: 'Galeria',
  description: 'Galeria de fotos com lightbox',
  category: 'visual',
  multiple: false,
  icon: ImageIcon,
  defaultProps: { title: 'Galeria' },
  Render: GalleryRender,
};

// =============================================================================
// SocialBlock
// =============================================================================

interface SocialBlockProps {
  title?: string;
  links?: SocialLinksType;
}

function SocialRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as SocialBlockProps;
  const links = props.links || ctx.tenant.publicPage.socialLinks || {};
  const hasAny = Object.values(links).some((v) => !!v);
  if (!hasAny) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="text-center">
        {props.title && (
          <h2
            className="font-display text-2xl md:text-3xl font-extrabold mb-6"
            style={{ color: theme.primaryColor }}
          >
            {props.title}
          </h2>
        )}
        <div className="flex justify-center">
          <SocialLinks links={links} color={theme.primaryColor} />
        </div>
      </div>
    </BlockShell>
  );
}

export const socialBlockDefinition: BlockDefinition<SocialBlockProps> = {
  type: 'social',
  label: 'Redes sociais',
  description: 'Links para Instagram, Facebook, etc',
  category: 'interaction',
  multiple: false,
  icon: Globe,
  defaultProps: { title: 'Me siga' },
  Render: SocialRender,
};
